/**
 * StateScopeContext
 *
 * One source of truth for "is the current user in a restricted state?"
 * Fetches GET /api/auth/state-scope once after login, plus the
 * per-state branding row (/api/admin/state-branding/:code), and
 * exposes:
 *    isRestricted        bool
 *    displayName         string   ("Texas", "Climate Action DC", …)
 *    stateCode           string   ("TX", "CLIMATE ACTION DC", …)
 *    logoUrl             string | null   (data: URI or remote)
 *    theme               "orb_green" | "orb_black" | "orb_white"
 *    brandLabel          derived: displayName if restricted, else "The Uplift"
 *    reload()            refetch (call after customizations save)
 *
 * Consumers: Layout header, HomePage welcome, AuthPage portal check,
 * Customization page.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

const StateScopeContext = createContext({
  isRestricted: false,
  displayName: null,
  stateCode: null,
  logoUrl: null,
  theme: 'orb_green',
  brandLabel: 'Uplift',
  reload: () => {},
});

export const useStateScope = () => useContext(StateScopeContext);

export const StateScopeProvider = ({ children }) => {
  const { api, user, isAuthenticated } = useAuth();
  const [scope, setScope] = useState({
    isRestricted: false,
    displayName: null,
    stateCode: null,
    logoUrl: null,
    theme: 'orb_green',
  });

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setScope({ isRestricted: false, displayName: null, stateCode: null, logoUrl: null, theme: 'orb_green' });
      return;
    }
    try {
      const r = await api.get('/auth/state-scope');
      const s = r.data || {};
      // Prefer theme/logo from state-scope (works for all users).
      // Admin branding endpoint (below) wins for supervisors/admins
      // because the Customization page lets them preview uncommitted
      // changes — but is strictly not required for the theme to apply.
      let logo_url = s.logo_url || null;
      let theme = s.theme || 'orb_green';
      if (s.is_restricted && s.state) {
        try {
          const b = await api.get(`/admin/state-branding/${encodeURIComponent(s.state)}`);
          logo_url = b.data?.logo_url || logo_url;
          theme = b.data?.theme || theme;
        } catch {
          /* non-admin / non-supervisor — already have defaults from
             state-scope, no action needed. */
        }
      }
      setScope({
        isRestricted: !!s.is_restricted,
        displayName: s.display_name || null,
        stateCode: s.state || null,
        logoUrl: logo_url,
        theme,
      });
    } catch {
      /* ignore — user without state, etc. */
    }
  }, [api, isAuthenticated, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const brandLabel = scope.isRestricted && scope.displayName
    ? scope.displayName
    : 'Uplift';

  // Apply the white theme at the root so every page inherits the
  // palette without needing custom CSS per page.
  useEffect(() => {
    const root = document.documentElement;
    root.removeAttribute('data-orb-theme');
    if (scope.isRestricted && scope.theme === 'orb_white') {
      root.setAttribute('data-orb-theme', 'orb-white');
    }
  }, [scope.isRestricted, scope.theme]);

  return (
    <StateScopeContext.Provider value={{ ...scope, brandLabel, reload: load }}>
      {children}
    </StateScopeContext.Provider>
  );
};
